import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep, keyBy, orderBy } from 'lodash';
import { Box, Flex, Input, Tooltip, Text } from '@chakra-ui/react';

import ColorPicker, { allColors } from './ColorPicker';
import { CirlceIcon, allIcons } from '../utils/Icon';
import { getRandom, indexOfObject } from '../../helpers';
import DragDropList from '../dragAndDrop/DragDropList';
import { TState, useActions } from '../../redux/store';
import RSelect from './RSelect';

export type TSelectInputData = {
  value: string;
  color: string;
  order: number;
  disabled: boolean;
  id: number;
  roles?: string[];
};

type TSelectInput = {
  setFormData: any;
  orderedOptions?: TSelectInputData[];
};

const emptyValueAtIndex = (v: any[], idx: number) => {
  const newArray = cloneDeep(v);
  const index = indexOfObject(v, 'order', idx);
  newArray[index].disabled = !newArray[index].disabled;
  return newArray;
};

const Item = ({
  item: { value, color, disabled, roles },
  roleList,
  idx,
  setItems,
  updateItem,
}: {
  item: TSelectInputData;
  idx: number;
  roleList: { label: string; value: any }[];
  setItems: (v: any) => any;
  updateItem: (idx: number, v: any) => any;
}) => {
  const rolesById = keyBy(roleList, 'value');

  const handleChange = (val: { label: string; value: any }[]) => {
    updateItem(
      idx,
      val.map((v) => v.value),
    );
  };

  const itemRoles = roles?.map((role) => rolesById[role]);

  return (
    <Flex alignItems='center' key={idx} my={1}>
      <Input
        isDisabled={disabled}
        key={`${idx}-${value}`}
        type='text'
        defaultValue={value || ''}
        mr={2}
        onBlur={(e) =>
          setItems((v: TSelectInputData[]) => {
            const newV = cloneDeep(v);
            const index = indexOfObject(newV, 'order', idx);
            newV[index].value = e.target.value;
            return newV;
          })
        }
        flex={1}
      />
      <Box mr={2} minW='25%' maxW='50%'>
        <RSelect
          isMulti
          placeholder='Visible par tous les rôles'
          value={itemRoles}
          onChange={handleChange}
          options={roleList}
        />
      </Box>
      <ColorPicker
        bg={color || 'transparent'}
        onChange={(e) =>
          setItems((c: TSelectInputData[]) => {
            const newColors = cloneDeep(c || []);
            const index = indexOfObject(newColors, 'order', idx);
            newColors[index].color = e.hex;
            return newColors;
          })
        }
      />
      <Tooltip label={disabled ? 'Activer' : 'Désactiver'}>
        <span>
          <CirlceIcon
            ml={3}
            as={disabled ? allIcons.HiCheck : allIcons.HiTrash}
            onClick={() => {
              setItems((v: any) => emptyValueAtIndex(v, idx));
            }}
          />
        </span>
      </Tooltip>
    </Flex>
  );
};

const SelectInput = ({ orderedOptions = [], setFormData }: TSelectInput) => {
  const theme = useSelector((state: TState) => state.theme);
  const roleList = useSelector((state: TState) => state.roleList);
  const { getRolesList } = useActions();

  const listRoles: { name: string; _id: string }[] = useMemo(
    () => [
      { name: `admin ${localStorage.getItem('company')}`, _id: '000' },
      ...roleList,
    ],
    [roleList],
  );

  const [items, setItems] = useState<TSelectInputData[]>(orderedOptions);

  useEffect(() => {
    setFormData(items);
  }, [items]);

  useEffect(() => {
    getRolesList();
  }, []);

  const updateItem = (idx: number, roles: string[]) => {
    const newItems = cloneDeep(items);
    const index = indexOfObject(newItems, 'order', idx);
    newItems[index].roles = roles;
    setItems(newItems);
  };

  return (
    <Box position='relative'>
      <Text
        top='-15px'
        position='absolute'
        textAlign='right'
        fontStyle='italic'
        fontSize={theme.fontSize.xSmall}
        style={{ textWrap: 'nowrap' }}
        right='100px'
      >
        {'Vider le champ pour rendre visible pour tous'}
      </Text>
      <DragDropList
        listItem={orderBy(items, 'order')}
        List={({ item, idx }) => (
          <Item
            item={item}
            idx={idx}
            setItems={setItems}
            roleList={listRoles.map((role) => ({
              label: role.name,
              value: role._id,
            }))}
            updateItem={updateItem}
          />
        )}
        type={'SELECT_LIST'}
        onDrop={(newOrder) => {
          const newItems = items.map((opt, idx) => ({
            ...opt,
            order: indexOfObject(newOrder, 'id', idx),
          }));

          setItems(newItems);
        }}
      />
      <CirlceIcon
        my={3}
        as={allIcons.HiPlusSmall}
        onClick={() => {
          setItems((v) => [
            ...(v || []),
            {
              value: '',
              color: getRandom(allColors),
              order: items.length,
              id: items.length,
              disabled: false,
            },
          ]);
        }}
      />
    </Box>
  );
};

export default SelectInput;
